import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useOrgs } from '../api/orgs';
import { useProducts } from '../api/products';
import {
  emptyClientDrivenDefinition,
  useCreateWorkflow,
  type WorkflowMode,
} from '../api/workflows';

export function WorkflowNewPage() {
  const navigate = useNavigate();
  const orgs = useOrgs();
  const products = useProducts();
  const create = useCreateWorkflow();
  // Org-products page (and any other in-app "create workflow"
  // CTA) deep-links here with `?org_id=…&product_id=…` so the
  // form opens with the right slot pre-selected. The drop-downs
  // accept whatever's passed; if it doesn't match a known org or
  // product the empty placeholder shows instead and the operator
  // picks manually.
  const [searchParams] = useSearchParams();
  const prefilledOrgId = searchParams.get('org_id') ?? '';
  const prefilledProductId = searchParams.get('product_id') ?? '';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [orgId, setOrgId] = useState(prefilledOrgId);
  const [productId, setProductId] = useState(prefilledProductId);
  // Product-wide default. When checked, this workflow applies to
  // every org that uses the named product until they author their
  // own override. Mirrors the question-set defaults flow. The
  // resolver in orchestrator (`findActiveFor`) prefers an
  // org-specific row over a default; only cross-tenant admins can
  // publish defaults.
  const [isDefault, setIsDefault] = useState(false);
  // client_driven is the default for the new editor — that's what the
  // four new step kinds are designed for. `active` (server-driven) is
  // here for parity with templates that mix kinds.
  const [defaultMode, setDefaultMode] = useState<WorkflowMode>('client_driven');
  const [workflowTimeoutSeconds, setWorkflowTimeoutSeconds] = useState<number | string>(86400);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (!isDefault && !orgId) {
      setError('Organisation is required (or check "Default for all organisations").');
      return;
    }
    if (!productId) {
      setError('Product is required.');
      return;
    }
    const timeout = Number(workflowTimeoutSeconds);
    if (!Number.isFinite(timeout) || timeout < 60) {
      setError('Workflow timeout must be at least 60 seconds.');
      return;
    }

    create.mutate(
      {
        name: trimmedName,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(isDefault ? { is_default: true } : { org_id: orgId }),
        product_id: productId,
        default_mode: defaultMode,
        workflow_timeout_seconds: timeout,
        // Seed with the minimal valid client-driven definition — a single
        // `output` step. The editor (PRs 3.2+) lets the operator add real
        // steps and connect them.
        definition: emptyClientDrivenDefinition(trimmedName),
      },
      {
        onSuccess: (created) => {
          notifications.show({ color: 'green', message: 'Workflow draft created.' });
          navigate(`/workflows/${created.id}/edit`);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            const parsed = (() => {
              try {
                return JSON.parse(err.body) as { message?: string };
              } catch {
                return null;
              }
            })();
            setError(parsed?.message ?? `Create failed (${err.status})`);
            return;
          }
          setError(err instanceof Error ? err.message : 'Create failed');
        },
      },
    );
  }

  return (
    <Stack gap="lg" maw={600}>
      <Anchor component={Link} to="/workflows" size="sm">
        ← Back to workflows
      </Anchor>
      <Title order={2}>New workflow</Title>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g. Skin lesion triage"
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Textarea
            label="Description"
            placeholder="Optional. What does this workflow do?"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Checkbox
            label="Default for all organisations"
            description="When checked, this workflow applies to every organisation that uses the selected product, unless they author their own override. Requires cross-tenant admin."
            checked={isDefault}
            onChange={(e) => setIsDefault(e.currentTarget.checked)}
          />
          {!isDefault ? (
            <NativeSelect
              label="Organisation"
              description="Workflow owner. Cross-tenant admins must pick the target org."
              required
              value={orgId}
              onChange={(e) => setOrgId(e.currentTarget.value)}
              data={[
                { value: '', label: 'Select an organisation…' },
                ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          ) : (
            <Text size="sm" c="dimmed">
              Organisation: <em>none — applies to every org for the selected product.</em>
            </Text>
          )}
          <NativeSelect
            label="Product"
            required
            value={productId}
            onChange={(e) => setProductId(e.currentTarget.value)}
            data={[
              { value: '', label: 'Select a product…' },
              ...(products.data ?? []).map((p) => ({ value: p.id, label: p.displayName })),
            ]}
          />
          <NativeSelect
            label="Default mode"
            value={defaultMode}
            onChange={(e) => setDefaultMode(e.currentTarget.value as WorkflowMode)}
            data={[
              { value: 'client_driven', label: 'client_driven (operator authors steps)' },
              { value: 'active', label: 'active (server-driven engine)' },
            ]}
          />
          <NumberInput
            label="Workflow timeout (seconds)"
            description="Wall-clock cap from start to terminal step"
            min={60}
            step={60}
            required
            value={workflowTimeoutSeconds}
            onChange={(v) => setWorkflowTimeoutSeconds(v)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" component={Link} to="/workflows">
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create draft
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
}
